import React, { useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { api } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import { optionsStatusProps, useOptions } from '../hooks/useOptions';
import { usePolicyList, useSubmit } from '../hooks/usePolicyList';
import { ResultViewer } from '../components/ResultViewer';
import { PolicyModal } from '../components/PolicyModal';
import { UserFields } from '../components/UserFields';
import { deleteColumn } from '../components/deleteColumn';
import { USER_USER_GROUP_COLUMNS, UserUserGroupRow, toNamedOptions, toUserUserGroupRows } from './policyRows';

export const UserUserGroupPage: React.FC<{ app: string }> = ({ app }) => {
  const { base, loading, result, reload } = usePolicyList(app, 'user_user_group');
  const { addNotification } = useNotifications();
  const rows = useMemo(() => toUserUserGroupRows(result?.data), [result]);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [userType, setUserType] = useState('PERSON');
  const [userValue, setUserValue] = useState('');
  const [groupName, setGroupName] = useState('');
  const groups = useOptions(`/${encodeURIComponent(app)}/user_groups`, toNamedOptions);
  const create = useSubmit();

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<UserUserGroupRow | null>(null);
  const del = useSubmit();

  const columns = useMemo(
    () => [
      ...USER_USER_GROUP_COLUMNS,
      deleteColumn<UserUserGroupRow>(
        r => (r.userType && r.user && r.userGroup ? `${r.userType} ${r.user} from ${r.userGroup}` : null),
        r => {
          del.setError(null);
          setPendingDelete(r);
        }
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setError and setPendingDelete are stable
    []
  );

  if (!app) return <Alert type="info">Select an application from the top bar.</Alert>;

  const openCreate = () => {
    setUserType('PERSON');
    setUserValue('');
    setGroupName('');
    create.setError(null);
    setCreateOpen(true);
    // Fetch fresh each time so groups created elsewhere show up.
    groups.load();
  };

  const confirmCreate = async () => {
    const value = userValue.trim();
    const ok = await create.submit(() =>
      api('PUT', base, {
        users: [{ type: userType, value }],
        user_groups: [{ name: groupName }],
      })
    );
    if (ok) {
      setCreateOpen(false);
      addNotification('success', `Added ${userType} ${value} to ${groupName}`);
      await reload();
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { userType: type, user, userGroup } = pendingDelete;
    const ok = await del.submit(() =>
      api('DELETE', base, { user: { type, value: user }, user_group: { name: userGroup } })
    );
    if (ok) {
      setPendingDelete(null);
      addNotification('success', `Removed ${type} ${user} from ${userGroup}`);
      await reload();
    }
  };

  const selectedGroup = groupName
    ? groups.state.items.find(o => o.value === groupName) ?? { value: groupName, label: groupName }
    : null;
  const canCreate = userValue.trim() !== '' && groupName !== '';

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <Button variant="primary" onClick={openCreate}>
            Create Policy
          </Button>
        }
      >
        User - UserGroup Policy
      </Header>

      {loading && !result ? (
        <StatusIndicator type="loading">Loading policies</StatusIndicator>
      ) : (
        <ResultViewer data={result?.data} error={result?.error} rows={rows} columns={columns} title="Policy" />
      )}

      <PolicyModal
        visible={createOpen}
        header="Create policy"
        confirmText="Create"
        busy={create.busy}
        confirmDisabled={!canCreate}
        error={create.error}
        errorHeader="Create failed"
        onCancel={() => setCreateOpen(false)}
        onConfirm={confirmCreate}
      >
        <UserFields userType={userType} user={userValue} onUserTypeChange={setUserType} onUserChange={setUserValue} />
        <FormField label="User Group">
          <Select
            selectedOption={selectedGroup}
            onChange={({ detail }) => setGroupName(detail.selectedOption.value ?? '')}
            options={groups.state.items}
            {...optionsStatusProps(groups.state, 'user groups')}
            placeholder="Choose a user group"
            ariaLabel="User Group"
          />
        </FormField>
      </PolicyModal>

      <PolicyModal
        visible={pendingDelete !== null}
        header="Delete policy"
        confirmText="Delete"
        busy={del.busy}
        error={del.error}
        errorHeader="Delete failed"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      >
        {pendingDelete && (
          <Box>
            Remove <b>{pendingDelete.userType} {pendingDelete.user}</b> from user group{' '}
            <b>{pendingDelete.userGroup}</b> in <b>{app}</b>?
          </Box>
        )}
      </PolicyModal>
    </SpaceBetween>
  );
};
